import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Loader2, Check, X as XIcon, Play, Star, User } from "lucide-react";
import { MemberLink } from "@/components/build/MemberLink";
import { ProfileAvatar } from "@/components/build/ProfileAvatar";
import { useNotes } from "@/components/notes/NotesHub";
import { WorkSampleLightbox } from "@/components/WorkSampleViewer";
import { ConfirmDialog } from "./ConfirmDialog";

const STATUS_LABEL = { pending: "Pending", shortlisted: "Shortlisted", accepted: "Accepted", declined: "Declined", withdrawn: "Withdrawn" };
const STATUS_CLS = { accepted: "bg-emerald-100 text-emerald-700", declined: "bg-red-100 text-red-600", withdrawn: "bg-hairline text-faint", shortlisted: "bg-amber-100 text-amber-700" };
const ACTIONABLE = ["pending", "shortlisted"];
const DISCLAIMER_TEXT = "Payment is arranged directly by the Member Collaboration's creator. NJM is not a party to the payment.";

/**
 * Collaboration Opportunities Phase 2 (2026-08) — the creator's review queue
 * (GET /v1/collaboration/opportunities/{id}/applications, row-ownership check server-side).
 * Visible ONLY to the opportunity's own creator — never rendered for anyone else.
 * Wave 2 (Nikhil, 2026-09-30) — added Shortlist (pending -> shortlisted only), an explicit
 * "View Profile" link, `availability`, and a disclaimer-carrying confirm on Accept for PAID
 * Member Collaborations only (`compensation`/`kind` passed down from the detail modal).
 */
export function CollaborationApplicationsPanel({ opportunityId, roleSlots, compensation, kind, groupByRole, onDecided }) {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [viewingWork, setViewingWork] = useState(null);
  const { openSendNote } = useNotes();
  const showDisclaimer = compensation === "paid" && kind === "member";

  const load = () => api.get(`/v1/collaboration/opportunities/${opportunityId}/applications`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [opportunityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (application, action) => {
    setBusyId(application.id);
    try {
      await api.post(`/v1/collaboration/applications/${application.id}/${action}`);
      toast.success(action === "accept" ? "Application accepted" : action === "shortlist" ? "Applicant shortlisted" : "Application declined");
      load();
      onDecided?.();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code === "slot_full" ? "That role is already full" : "Couldn't update this application");
    } finally {
      setBusyId(null);
    }
  };

  if (items === null) return <div className="flex justify-center py-6" data-testid="collaboration-applications-loading"><Loader2 className="h-4 w-4 animate-spin text-faint" /></div>;
  if (items.length === 0) return <div className="font-mont text-xs text-faint py-3" data-testid="collaboration-applications-empty">No applications yet.</div>;

  const roleBySlot = Object.fromEntries((roleSlots || []).map((s) => [s.slot_id, s.role]));

  const renderApplication = (a) => (
        <div key={a.id} className="rounded-xl border border-black/10 p-3.5 bg-paper" data-testid={`collaboration-application-${a.id}`}>
          <div className="flex items-center gap-2.5">
            <ProfileAvatar freelancerId={a.applicant_freelancer_id} hasPhoto={Boolean(a.applicant_photo)} alt=""
              className="h-8 w-8 rounded-full object-cover shrink-0"
              fallback={<div className="h-8 w-8 rounded-full bg-brand text-ink flex items-center justify-center font-mont text-[11px] font-bold shrink-0">{(a.applicant_name || "?")[0]}</div>} />
            <div className="min-w-0 flex-1">
              <MemberLink freelancerId={a.applicant_freelancer_id} className="font-mont text-[13px] font-bold text-ink hover:underline" testId={`collaboration-applicant-link-${a.id}`}>{a.applicant_name}</MemberLink>
              <div className="font-mont text-[10.5px] text-faint">Applied for {roleBySlot[a.slot_id] || "a role"}</div>
            </div>
            <span className={`shrink-0 rounded-full px-2 py-0.5 font-mont text-[9.5px] font-extrabold uppercase tracking-wider ${STATUS_CLS[a.status] || "bg-brand/25 text-ink"}`}>
              {STATUS_LABEL[a.status] || a.status}
            </span>
          </div>
          {a.availability && <p className="mt-2 font-mont text-[11px] text-ink-soft" data-testid={`collaboration-application-availability-${a.id}`}><span className="font-bold text-ink">Availability:</span> {a.availability}</p>}
          {a.note && <p className="mt-2.5 font-mont text-[12px] text-ink-soft leading-relaxed" data-testid={`collaboration-application-note-${a.id}`}>{a.note}</p>}
          {a.work_samples?.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid={`collaboration-application-samples-${a.id}`}>
              {a.work_samples.map((s) => (
                <button key={s.id} type="button" onClick={() => setViewingWork(s)} data-testid={`collaboration-application-sample-${s.id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-black/12 px-2.5 py-1 font-mont text-[10px] text-ink-soft hover:border-ink/40 max-w-full">
                  <Play className="h-2.5 w-2.5 shrink-0" /> <span className="truncate max-w-[160px]">{s.title || "Selected work"}</span>
                </button>
              ))}
            </div>
          )}
          {a.application_work_url && (
            <div className="mt-2.5" data-testid={`collaboration-application-work-link-${a.id}`}>
              <button type="button"
                onClick={() => setViewingWork({ id: `${a.id}_work_url`, url: a.application_work_url, embed_url: a.application_work_embed_url, media_kind: a.application_work_media_kind, role_tag: `Relevant work for this ${kind === "njm" ? "NJM Collaboration" : "Member Collaboration"}` })}
                data-testid={`collaboration-application-work-link-open-${a.id}`}
                className="inline-flex items-center gap-1 rounded-full border border-brand/50 bg-[#FFFCF2] px-2.5 py-1 font-mont text-[10px] font-semibold text-[#8A6A00] hover:border-brand max-w-full">
                <Play className="h-2.5 w-2.5 shrink-0" /> <span className="truncate max-w-[220px]">Relevant work for this {kind === "njm" ? "NJM Collaboration" : "Member Collaboration"} ↗</span>
              </button>
              {a.work_note && <p className="mt-1.5 font-mont text-[11.5px] text-ink-soft leading-relaxed italic" data-testid={`collaboration-application-work-note-${a.id}`}>"{a.work_note}"</p>}
            </div>
          )}
          {ACTIONABLE.includes(a.status) && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {a.status === "pending" && (
                <button onClick={() => decide(a, "shortlist")} disabled={busyId === a.id} data-testid={`collaboration-application-shortlist-${a.id}`}
                  className="inline-flex items-center gap-1 rounded-lg border border-amber-300 px-3 py-1.5 font-mont text-[11px] font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                  <Star className="h-3 w-3" /> Shortlist
                </button>
              )}
              {showDisclaimer ? (
                <ConfirmDialog
                  testIdPrefix={`collaboration-application-accept-${a.id}`}
                  title="Accept this applicant?"
                  description={DISCLAIMER_TEXT}
                  confirmLabel="Accept"
                  onConfirm={() => decide(a, "accept")}
                  trigger={
                    <button disabled={busyId === a.id} data-testid={`collaboration-application-accept-${a.id}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-ink px-3.5 py-1.5 font-mont text-[11px] font-bold text-paper hover:bg-ink/85 disabled:opacity-50">
                      <Check className="h-3 w-3" /> Accept
                    </button>
                  }
                />
              ) : (
                <button onClick={() => decide(a, "accept")} disabled={busyId === a.id} data-testid={`collaboration-application-accept-${a.id}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-ink px-3.5 py-1.5 font-mont text-[11px] font-bold text-paper hover:bg-ink/85 disabled:opacity-50">
                  <Check className="h-3 w-3" /> Accept
                </button>
              )}
              <button onClick={() => decide(a, "decline")} disabled={busyId === a.id} data-testid={`collaboration-application-decline-${a.id}`}
                className="inline-flex items-center gap-1 rounded-lg border border-black/12 px-3.5 py-1.5 font-mont text-[11px] font-semibold text-ink-soft hover:border-red-300 hover:text-red-600 disabled:opacity-50">
                <XIcon className="h-3 w-3" /> Decline
              </button>
              {a.applicant_freelancer_id && (
                <MemberLink freelancerId={a.applicant_freelancer_id} className="font-mont text-[10.5px] font-semibold uppercase tracking-wider text-faint hover:text-ink transition-colors inline-flex items-center gap-1" testId={`collaboration-application-view-profile-${a.id}`}>
                  <User className="h-3 w-3" /> View Profile
                </MemberLink>
              )}
              {openSendNote && a.applicant_freelancer_id && (
                <button onClick={() => openSendNote({ id: a.applicant_freelancer_id, name: a.applicant_name, profile_image: a.applicant_photo, primary_role: roleBySlot[a.slot_id] }, { source: "collaboration", context: { type: "collaboration", ref_id: opportunityId } })}
                  data-testid={`collaboration-application-send-note-${a.id}`}
                  className="ml-auto font-mont text-[10.5px] font-semibold uppercase tracking-wider text-faint hover:text-brand-dim transition-colors">
                  Send note →
                </button>
              )}
            </div>
          )}
          {!ACTIONABLE.includes(a.status) && (openSendNote || a.applicant_freelancer_id) && (
            <div className="mt-3 flex items-center gap-3">
              {a.applicant_freelancer_id && (
                <MemberLink freelancerId={a.applicant_freelancer_id} className="font-mont text-[10.5px] font-semibold uppercase tracking-wider text-faint hover:text-ink transition-colors inline-flex items-center gap-1" testId={`collaboration-application-view-profile-${a.id}`}>
                  <User className="h-3 w-3" /> View Profile
                </MemberLink>
              )}
              {openSendNote && a.applicant_freelancer_id && (
                <button onClick={() => openSendNote({ id: a.applicant_freelancer_id, name: a.applicant_name, profile_image: a.applicant_photo, primary_role: roleBySlot[a.slot_id] }, { source: "collaboration", context: { type: "collaboration", ref_id: opportunityId } })}
                  data-testid={`collaboration-application-send-note-${a.id}`}
                  className="ml-auto font-mont text-[10.5px] font-semibold uppercase tracking-wider text-faint hover:text-brand-dim transition-colors">
                  Send note →
                </button>
              )}
            </div>
          )}
        </div>
  );

  if (!groupByRole) {
    return (
      <div className="space-y-2.5" data-testid="collaboration-applications-panel">
        {items.map(renderApplication)}
        {viewingWork && <WorkSampleLightbox sample={viewingWork} onClose={() => setViewingWork(null)} />}
      </div>
    );
  }

  const groups = {};
  items.forEach((a) => { (groups[a.slot_id] = groups[a.slot_id] || []).push(a); });
  const slotOrder = (roleSlots || []).map((s) => s.slot_id).filter((sid) => groups[sid]?.length);
  const ungrouped = Object.keys(groups).filter((sid) => !slotOrder.includes(sid));

  return (
    <div className="space-y-5" data-testid="collaboration-applications-panel-grouped">
      {[...slotOrder, ...ungrouped].map((sid) => (
        <div key={sid} data-testid={`collaboration-applications-role-group-${sid}`}>
          <div className="mb-2 font-mont text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-faint">
            {roleBySlot[sid] || "Role"} <span className="text-ink-soft">· {groups[sid].length}</span>
          </div>
          <div className="space-y-2.5">{groups[sid].map(renderApplication)}</div>
        </div>
      ))}
      {viewingWork && <WorkSampleLightbox sample={viewingWork} onClose={() => setViewingWork(null)} />}
    </div>
  );
}
